import { ChangeDetectionStrategy, Component, ElementRef, AfterViewInit, OnChanges, computed, inject, input, signal, viewChild } from '@angular/core';
import { LoggerService } from '@core/services';
import QRCode from 'qrcode';

/**
 * Renders an `otpauth://` URI as a QR code (task 5.1).
 *
 * The URI is generated by the BACKEND (`/parent-auth/mfa/enroll` returns
 * `otpauthUri`) and contains the TOTP secret. It is held in component state
 * only - never persisted, never logged. The QR is drawn locally on a canvas
 * (no third-party image API - sending the secret to a QR web service would
 * leak the enrollment secret to a third party).
 *
 * Uses the battle-tested `qrcode` npm package (user-approved dependency,
 * 2026-09-09): the previous hand-rolled encoder produced invalid QRs for
 * version 4+ payloads (wrong Reed-Solomon generator polynomial, hardcoded
 * 7 EC codewords, no multi-block interleaving) - the otpauth URI (~150
 * chars, version 7) never scanned. QRCode.toCanvas handles all versions
 * and EC levels correctly.
 */
@Component({
  selector: 'app-otpauth-qr',
  standalone: true,
  template: `<canvas #qrCanvas class="qr-canvas" [attr.data-testid]="'mfa-qr-canvas'"></canvas>`,
  styles: [
    `
      :host {
        display: block;
      }
      .qr-canvas {
        display: block;
        border: 8px solid #fff;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtpauthQrComponent implements AfterViewInit, OnChanges {
  private readonly logger = inject(LoggerService);

  /** The otpauth:// URI from the backend - component state only. */
  readonly otpauthUri = input<string>('');

  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('qrCanvas');
  private readonly rendered = signal(false);

  private readonly renderKey = computed(() => {
    // Re-render whenever the URI changes and the view is ready.
    return `${this.otpauthUri()}|${this.rendered()}`;
  });

  constructor() {
    void this.renderKey();
    queueMicrotask(() => this.draw());
  }

  ngAfterViewInit(): void {
    this.rendered.set(true);
    this.draw();
  }

  ngOnChanges(): void {
    if (this.rendered()) {
      queueMicrotask(() => this.draw());
    }
  }

  /** Draw the QR code onto the canvas via the qrcode package. */
  private draw(): void {
    const uri = this.otpauthUri();
    const canvas = this.canvasRef()?.nativeElement;
    if (!uri || !canvas) {
      return;
    }

    QRCode.toCanvas(canvas, uri, {
      errorCorrectionLevel: 'L',
      margin: 4,
      scale: 4,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then(() => {
        // Clear any previous failure state on success.
        canvas.style.opacity = '1';
      })
      .catch((err: unknown) => {
        this.logger.error('QR code rendering failed', err);
      });
  }
}
